import React, { FC, useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedMarker, Map, MapMouseEvent, useMap } from '@vis.gl/react-google-maps';
import { type Marker, MarkerClusterer } from '@googlemaps/markerclusterer';
import Icon from '../../../@shared/icon';
import { iconSize } from '../../../../constants/icon.constants';
import { windowService } from '../../../../utils/window.utils.js';
import { isGeometryDocType } from '../../dealerLocator.utils';
import { SearchType, TDealerLocatorMapProps } from '../../types/dealerLocatorLanding.types';
import dealerPinSvg from '../../../../assets/dealer-pin.svg';
import dealerPinActiveSvg from '../../../../assets/dealer-pin-active.svg';
import userLocationSvg from '../../../../assets/user-location.svg';
import useContryBordersCoords from './hooks/useCountryBordersCoords.effect';
import AreaPolygonLabel from './components/AreaPolygonLabel';
import AreaPolygon from './components/AreaPolygon';
import useRegionBordersCoords from './hooks/useRegionBorderCoords.effect';
import { googleGeolocationClick } from '../../../../utils/analytics';
import { dealerLocatorGeolocationClick } from '../../../../constants/analytics.constants';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import DealerLocatorGeoLocatorToastMessage from '../dealerLocatorGeoLocationToastMessage';
import { DL_GOOGLE_MAP_ID, DL_MAP_ID } from '../../../../constants/dealerLocator.constants';
import { debounceHandler } from '../../../../utils/debounceHandler.utils';

const DealerLocatorMap: FC<TDealerLocatorMapProps> = ({
  model,
  country,
  region,
  dealers,
  place,
  dealerIdForPinWithHover,
  setPlacesService,
  setAutocompleteService,
  setDealerIdForPinWithHover,
  setClickedDealerNumber,
  setCountry,
  setRegion,
  setUserLocation,
  mapSettings,
  sitecoreContext,
  dealerIdPinHovered,
  resultsViewModeInTablet,
}) => {
  const { t } = useTranslation();
  const map: google.maps.Map | null = useMap(DL_MAP_ID);
  const [maxZindex, setMaxZindex] = useState<number | undefined>(undefined);
  const [label, setLabel] = useState<string | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [showGeoLocationErrorMessage, updateShowGeoLocationErrorMessage] = useState<boolean>(false);
  const [enteredDisabledStateOnce, updateEnteredDisabledStateOnce] = useState<boolean>(false);
  const countryIsoCodeWithFallback =
    model.searchType === SearchType.SearchByLocationOrDealer ? model.defaultCountry : country?.IsoCode || model.defaultCountry;
  const hasDealers = Array.isArray(dealers) ? dealers.length > 0 : !!dealers?.dealership;
  const dealersArray = (() => {
    if (hasDealers && Array.isArray(dealers)) {
      return dealers;
    } else if (hasDealers && !Array.isArray(dealers)) {
      return [dealers];
    }

    return [];
  })();

  const countriesPolygonData = useContryBordersCoords({ model });
  const regionsPolygonData = useRegionBordersCoords({ model, country });
  const hasVisibleCountryPolygon = countriesPolygonData?.length > 0 && !country;
  const hasVisibleRegionPolygon = regionsPolygonData?.length > 0 && !region;
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx.lg})`);
  const [markers, setMarkers] = useState<{ [key: string]: Marker }>({});
  const markersList = useRef<{ [key: string]: Marker }>({});
  const clusterer = useRef<MarkerClusterer | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const mapOptions = {
    disableDefaultUI: true,
  };

  useEffect(() => {
    if (map) {
      map?.setCenter({ lat: mapSettings.lat, lng: mapSettings.lng });
      map?.setZoom(mapSettings.zoom);
    }

    if (map && windowService()?.google) {
      setPlacesService(new google.maps.places.PlacesService(map));
      setAutocompleteService(new google.maps.places.AutocompleteService());
      setMaxZindex(google?.maps?.Marker?.MAX_ZINDEX);
    }
  }, [map, mapSettings]);

  const showUserLocation = useCallback(() => {
    const success = (position) => {
      setUserLocation({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      });
    };
    const error = (_) => {
      windowService()?.navigator.permissions.query({ name: "geolocation" }).then((permission) => {
          const permissionState = permission?.state;
          if (enteredDisabledStateOnce || permissionState === 'denied') {
            updateShowGeoLocationErrorMessage(true);
          } else {
            updateEnteredDisabledStateOnce(true);
          }
        });
    }
    windowService()?.navigator?.geolocation?.getCurrentPosition(success, error);
    googleGeolocationClick(
      dealerLocatorGeolocationClick,
      sitecoreContext?.analytics?.['region'],
      sitecoreContext?.analytics?.['language'],
      sitecoreContext?.analytics?.['market'],
      model?.searchType
    );
  }, [map, enteredDisabledStateOnce]);

  useEffect(() => {
    if (!map || model.searchType === SearchType.NoSearch) {
      return;
    }

    const hasDealers = Array.isArray(dealers) ? dealers.length > 0 : !!dealers?.dealership;

    if ((!hasDealers && !place) || !windowService()?.google) {
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    const dealersArray = (() => {
      if (hasDealers && Array.isArray(dealers)) {
        return dealers;
      } else if (hasDealers && !Array.isArray(dealers)) {
        return [dealers];
      }

      return [];
    })();

    dealersArray.forEach((dealer) => {
      if (dealer?.dealership?.latitude && dealer?.dealership?.longitude) {
        const position = {
          lat: Number.parseFloat(dealer.dealership.latitude),
          lng: Number.parseFloat(dealer.dealership.longitude),
        };
        bounds.extend(position);
      }
    });

    if (place) {
      bounds.extend(place);
    }

    const hasOnlySingleMarker = (dealersArray.length === 1 && !place) || (place && dealersArray.length === 0);

    // Restrict MAX ZOOM for fitBounds
    if (model.maxMapZoomForSingleResult && hasOnlySingleMarker) {
      google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
        map.setZoom(Math.min(map.getZoom() || 0, model.maxMapZoomForSingleResult));
      });
    }

    map?.fitBounds(bounds);
    markersList.current = {};
  }, [dealers, map, place, dealerIdPinHovered]);

  useEffect(() => {
    // ZOOM and PAN map to fit all States/Regions into map viewport
    if (!map || !hasVisibleRegionPolygon || !country) {
      return;
    }

    const selectedCountryPolygonData = countriesPolygonData.find((countryPolygonData) => countryPolygonData.code === country?.IsoCode);

    if (!selectedCountryPolygonData) {
      return;
    }

    let coordinates;
    if (isGeometryDocType(selectedCountryPolygonData?.kml)) {
      const kml = selectedCountryPolygonData?.kml;
      coordinates = kml?.geometry?.coordinates[0]?.map((pos) => ({ lat: pos[1], lng: pos[0] }));
    } else {
      const kml = selectedCountryPolygonData?.kml;
      coordinates = kml?.geometries?.flatMap((geometry) => geometry.coordinates[0]?.map((pos) => ({ lat: pos[1], lng: pos[0] })));
    }

    if (coordinates && coordinates.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      coordinates.forEach((coord) => bounds.extend(coord));
      map?.fitBounds(bounds);
    }
  }, [map, hasVisibleRegionPolygon, countriesPolygonData, country]);

  
  const handleMapClick = (mapMouseEvent: MapMouseEvent) => {
    const isCountrySelected =
      model.searchType === SearchType.SearchByLocationOrDealer
        ? model.defaultCountry
        : country?.IsoCode;

    if (isCountrySelected) {
      setUserLocation({
        lat: (mapMouseEvent?.detail?.latLng?.lat || '').toString(),
        lng: (mapMouseEvent?.detail?.latLng?.lng || '').toString(),
      });
    }
  };


  const getMarkerLabel = (text: string) => {
    return <span className="dealer-locator-landing__map-dealer-pin-label">{text}</span>;
  };

  useEffect(() => {
    if (!map) return;
    if (!clusterer.current && model.hasClusters) {
      clusterer.current = new MarkerClusterer({
        map,
        renderer: {
          render: ({ count, position }) => {
            const el = document.createElement('div');
            // Change appearance based on current zoom
            el.className = 'dealer-locator-landing__marker-cluster';

            const countEl = document.createElement('p');
            countEl.className = 'dealer-locator-landing__marker-cluster-text';
            countEl.textContent = String(count);

            el.appendChild(countEl);

            // Return AdvancedMarkerElement
            return new google.maps.marker.AdvancedMarkerElement({
              position: position,
              content: el,
            });
          },
        },
      });
    }
  }, [map]);

  useEffect(() => {
    if (!clusterer.current) return;
    clusterer.current?.clearMarkers();
    clusterer.current?.addMarkers(Object.values(markers));
  }, [clusterer.current, markers]);

  // this callback will effectively get passsed as ref to the markers to keep
  // tracks of markers currently on the map
  const setMarkerRef = useCallback((marker: Marker | null, key: string, isLast: boolean) => {
    if ((marker && markersList.current[key]) || (!marker && !markersList.current[key])) {
      if (isLast) {
        setMarkers(markersList.current);
      }
      return;
    }

    if (marker) {
      markersList.current = { ...markersList.current, [key]: marker };
    }

    if (isLast) {
      setMarkers(markersList.current);
    }
  }, []);

  return (
    <div
      className={`dealer-locator-landing__map ${resultsViewModeInTablet === 'list' ? 'dealer-locator-landing__map--list-mode' : ''}`}
      ref={mapContainerRef}
    >
      <DealerLocatorGeoLocatorToastMessage showGeoLocationErrorMessage={showGeoLocationErrorMessage} updateShowGeoLocationErrorMessage={updateShowGeoLocationErrorMessage} />
      <AreaPolygonLabel label={label} containerRef={mapContainerRef} />
      <Map
        defaultCenter={{ lat: mapSettings.lat, lng: mapSettings.lng }}
        defaultZoom={mapSettings.zoom}
        id={DL_MAP_ID}
        className="dealer-locator-landing__map-container"
        mapId={DL_GOOGLE_MAP_ID}
        onClick={(e) => {
          model.hasClickableMap &&
            debounceHandler(
              () => {
                handleMapClick(e);
              },
              timerRef,
              300
            );
        }}
        {...mapOptions}
      >
        {place && (
          <AdvancedMarker position={place} title={t('cnhi-dealerLocatorPlaceMarkerLabel')}>
            <img src={userLocationSvg} alt={t('cnhi-dealerLocatorPlaceMarkerLabel')} />
          </AdvancedMarker>
        )}
        {hasDealers &&
          dealersArray.map((dealer, i) => (
            <AdvancedMarker
              position={{ lat: Number.parseFloat(dealer.dealership.latitude), lng: Number.parseFloat(dealer.dealership.longitude) }}
              title={dealer.dealership.dealerName}
              key={dealer.dealership.dealerNumber}
              onMouseEnter={() => isWide && setDealerIdForPinWithHover(dealer.dealership.dealerNumber)}
              onMouseLeave={() => isWide && setDealerIdForPinWithHover(null)}
              onClick={() => {
                // Highlighting marker on click since mobile has no hover event listeners
                // Note: setState in this event handler is synchronous resulting in 2 times re-rendering. Order of setState to be preserved
                !isWide && setDealerIdForPinWithHover(dealer.dealership.dealerNumber);
                setClickedDealerNumber(dealer.dealership.dealerNumber);
              }}
              zIndex={dealerIdForPinWithHover !== dealer?.dealership?.dealerNumber ? undefined : maxZindex}
              ref={(marker) =>
                setMarkerRef(marker, dealer?.dealership?.dealerNumber, dealersArray.length === i + 1)
              }
            >
              {getMarkerLabel((i + 1).toString())}
              <img
                src={
                  dealerIdForPinWithHover !== dealer.dealership.dealerNumber
                    ? dealerPinSvg
                    : dealerPinActiveSvg
                }
                alt={dealer.dealership.dealerName}
              />
            </AdvancedMarker>
          ))}
        {hasVisibleCountryPolygon &&
          countriesPolygonData?.map((country) => (
            <AreaPolygon
              setLabel={setLabel}
              data={country}
              setCountry={setCountry}
              model={model}
              key={`country-polygons-${country.code}`}
            />
          ))}
        {hasVisibleRegionPolygon &&
          regionsPolygonData?.map((region) => (
            <AreaPolygon
              setLabel={setLabel}
              data={region}
              country={country}
              setRegion={setRegion}
              model={model}
              key={`region-polygons-${region.code}`}
            />
          ))}
      </Map>
      {model.searchByUserLocationEnabled && countryIsoCodeWithFallback && (
        <div className="dealer-locator-landing__map-icon dealer-locator-landing__map-icon--get-location" onClick={showUserLocation}>
          <Icon iconName="action-precision" color="primary" size={iconSize['lg']} />
        </div>
      )}
      <div
        className="dealer-locator-landing__map-icon dealer-locator-landing__map-icon--zoom-in"
        onClick={() => map?.setZoom((map?.getZoom() || 0) + 1)}
      >
        <Icon iconName="action-plus" color="light-black" size={iconSize['lg']} />
      </div>
      <div
        className="dealer-locator-landing__map-icon dealer-locator-landing__map-icon--zoom-out"
        onClick={() => map?.setZoom((map?.getZoom() || 1) - 1)}
      >
        <Icon iconName="action-minus" color="light-black" size={iconSize['lg']} />
      </div>
    </div>
  );
};

export default DealerLocatorMap;
